/**
 * Keys Folder Loader - Loads PK, FK, UNIQUE keys for a table subfolder
 */

import {
  DatabaseExplorerNode,
  MessageTreeNode,
  KeysFolderNode,
  createKeyNode,
  KeyNodeMetadata,
} from '../../explorer/models/DatabaseTreeModels';
import {
  createTableSubfolderEmptyNode,
  createTableSubfolderErrorNode,
} from './objectFolderUtils';
import { getKeys, KeyInfo, formatKeyDisplayName } from './keysService';
import { getEngineForConnection } from './tableExpansion';

export interface LoadKeysFolderOptions {
  node: KeysFolderNode;
  updateNode: (nodeId: string, updates: Partial<DatabaseExplorerNode>) => void;
  forceReload?: boolean;
}

/**
 * Loads the keys (PK, FK, UNIQUE) for a table when the Keys folder is expanded
 */
export async function loadKeysFolder({
  node,
  updateNode,
  forceReload = false,
}: LoadKeysFolderOptions) {
  if (!forceReload && node.children && node.children.length > 0) {
    const firstChild = node.children[0];
    if (firstChild && !['message', 'error'].includes(firstChild.type)) {
      return;
    }
  }

  const { connectionId, database, schema, tableName } = node.metadata;

  if (!database) {
    updateNode(node.id, {
      children: [
        createTableSubfolderErrorNode(
          connectionId, '', schema, tableName, 'keys-folder', node.id, 'Database not specified'
        ),
      ],
      isLoading: false,
    });
    return;
  }

  updateNode(node.id, {
    children: [
      {
        id: `loading-${connectionId}-${database}-${schema}-${tableName}-keys`,
        parentId: node.id,
        name: 'Loading keys...',
        type: 'message',
        canHaveChildren: false,
        children: [],
        isExpanded: false,
        icon: 'loading',
        metadata: { connectionId, database },
      } as MessageTreeNode,
    ],
    isLoading: true,
  });

  try {
    const engine = getEngineForConnection(connectionId);
    const result = await getKeys(connectionId, database, schema, tableName, engine);

    if (result.error) {
      updateNode(node.id, {
        children: [
          createTableSubfolderErrorNode(
            connectionId, database, schema, tableName, 'keys-folder', node.id, result.error
          ),
        ],
        isLoading: false,
      });
      return;
    }

    if (result.keys.length === 0) {
      updateNode(node.id, {
        children: [
          createTableSubfolderEmptyNode(
            connectionId, database, schema, tableName, 'keys-folder', node.id
          ),
        ],
        isLoading: false,
      });
      return;
    }

    const keyNodes = result.keys.map((key: KeyInfo) => {
      const displayName = formatKeyDisplayName(key);
      const metadata: KeyNodeMetadata = {
        connectionId,
        database,
        schema,
        tableName,
        keyName: key.name,
        keyType: key.keyType,
        columns: key.columns,
      };
      const keyNode = createKeyNode(metadata, node.id);
      keyNode.name = displayName;
      return keyNode;
    });

    updateNode(node.id, {
      children: keyNodes,
      isLoading: false,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to load keys';
    updateNode(node.id, {
      children: [
        createTableSubfolderErrorNode(
          connectionId, database, schema, tableName, 'keys-folder', node.id, message
        ),
      ],
      isLoading: false,
    });
  }
}
